/**
 * InternalDappRenderer Component
 *
 * Renders an internal minting dapp component directly (without iframe).
 * Internal dapps share the same React context and can use AppKit hooks directly.
 *
 * @module minting/components/InternalDappRenderer
 * @category Minting Framework
 */

import React, { useEffect, useState } from 'react';
import { useMintingDapp } from '../hooks/useMintingDapp';
import { AppContext } from '../../App';
import { useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import type { WalletState } from '../types';

/**
 * Props for InternalDappRenderer
 */
export interface InternalDappRendererProps {
  /** The ID of the minting dapp to render */
  dappId: string;
  /** Optional CSS class name */
  className?: string;
  /** Optional style overrides */
  style?: React.CSSProperties;
}

/**
 * InternalDappRenderer - Renders internal dapps as direct React components.
 *
 * Retrieves the dapp from the registry and renders its component with
 * injected AppKit context and wallet state.
 *
 * Automatically calls dapp.onInit and dapp.onDestroy lifecycle hooks if defined.
 *
 * @example
 * ```tsx
 * <InternalDappRenderer dappId="mint-nft" />
 * ```
 */
export const InternalDappRenderer: React.FC<InternalDappRendererProps> = ({
  dappId,
  className = '',
  style = {},
}) => {
  const dapp = useMintingDapp(dappId);
  const { appKit: appKitInstance } = React.useContext(AppContext);

  // Get wallet state using Reown AppKit hooks (correct API)
  const { address, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();

  // Debug logs
  console.log('[InternalDappRenderer] Render start:', {
    dappId,
    dappExists: !!dapp,
    dappType: dapp?.type,
    hasComponent: !!dapp?.component,
    appKitInstance: appKitInstance ? 'exists' : 'null',
    appKitType: typeof appKitInstance,
  });

  console.log('[InternalDappRenderer] Wallet state from hooks:', {
    address,
    chainId,
    isConnected,
  });

  const walletState: WalletState = {
    address,
    chainId: Number(chainId) || null,
    isConnected: isConnected || false,
  };

  // Call onInit lifecycle hook
  useEffect(() => {
    if (dapp?.onInit && dapp.component) {
      const props: any = {
        dappId: dapp.id,
        dappName: dapp.name,
        appKit: appKitInstance,
        ...walletState,
        sendMessage: (msg: any) => {
          // For internal dapps, sendMessage could post to window or use custom event
          console.log(`[${dappId}] Message:`, msg);
        },
      };
      dapp.onInit(dapp.id, props);
    }

    return () => {
      if (dapp?.onDestroy) {
        dapp.onDestroy(dapp.id);
      }
    };
  }, [dapp, appKitInstance, walletState]);

  // Render not found state
  if (!dapp) {
    return (
      <div className={`internal-dapp-not-found ${className}`} style={style}>
        <div className="text-center p-4">
          <i className="fas fa-exclamation-circle fa-2x text-warning mb-3"></i>
          <h5 className="text-light">Dapp Not Found</h5>
          <p className="text-light opacity-6 small">
            The minting application "{dappId}" is not registered.
          </p>
        </div>
      </div>
    );
  }

  // Render non-internal dapp error
  if (dapp.type !== 'internal' || !dapp.component) {
    return (
      <div className={`internal-dapp-error ${className}`} style={style}>
        <div className="text-center p-4">
          <i className="fas fa-times-circle fa-2x text-danger mb-3"></i>
          <h5 className="text-light">Invalid Dapp Type</h5>
          <p className="text-light opacity-6 small">
            This dapp is not an internal component and cannot be rendered directly.
          </p>
        </div>
      </div>
    );
  }

  // Render the internal component with injected props
  const Component = dapp.component;
  const props = {
    dappId: dapp.id,
    dappName: dapp.name,
    appKit: appKitInstance,
    // Map walletState to expected prop names
    account: walletState.address,
    chainId: walletState.chainId,
    isConnected: walletState.isConnected,
    walletState,
    sendMessage: (msg: any) => {
      // Could dispatch custom event or handle within parent
      console.log(`[${dappId}] Message:`, msg);
    },
  };

  return (
    <div
      className={`internal-dapp-renderer ${className}`}
      style={{ height: '100%', ...style }}
    >
      <Component {...props} />
    </div>
  );
};
